<template>
  <mui-table
    ref="MuiTable"
    class="p-datatable-sm"
    :value="dataList"
    :scrollable="scrollable"
    :scrollHeight="scrollHeight"
    :frozenWidth="compFrozenWidth"
    :expandableRowGroups="compGroupRowsBy != null"
    :rowGroupMode="compGroupRowsBy != null ? 'subheader' : ''"
    :groupRowsBy="compGroupRowsBy"
    :sortMode="sortMode"
    :sortField="sortField"
    :sortOrder="sortOrder"
    :removableSort="true"
    :expandedRowGroups.sync="expandedRowGroups"
    :virtualScroll="compGroupRowsBy == null"
    :lazy="compGroupRowsBy == null"
    :rows="20"
    :totalRecords="lazyTotalRecords"
    @virtual-scroll="onVirtualScroll" @sort="onSort"
  >
    <template #group-header="slotProps">
      <span>{{ slotProps.data[slotProps.name] }}</span>
    </template>
    <template #group-cell>
      <span />
    </template>
    <template #group-sum="slotProps">
      <span>{{ getNumberFormat(groupSum[slotProps.data[compGroupRowsBy[0]]][slotProps.name]) }}</span>
    </template>
    <template v-for="column in gridColumns">
      <mui-table-column
        :key="column.field"
        :_params="{isMeasure: column.isMeasure}"
        :column-key="column.field"
        :field="column.field"
        :header="column.header"
        :frozen="column.frozenYn === 'Y' || column.groupedYn === 'Y'"
        :header-style="column.headerStyle"
        :body-style="column.bodyStyle"
        :sortable="column.sortable"
      >
        <template #body="slotProps">
          <span
            v-if="column.isMeasure && column.threshold != null && slotProps.data[column.field] > Number(column.threshold)"
            :style="`color: ${column.textColorHigh};`"
          >
            {{ getNumberFormat(slotProps.data[column.field], column.numberFormat) }}
          </span>
          <span
            v-else-if="column.isMeasure && column.threshold != null"
            :style="`color: ${column.textColorLow};`"
          >
            {{ getNumberFormat(slotProps.data[column.field], column.numberFormat) }}
          </span>
          <span v-else-if="column.isMeasure">{{ getNumberFormat(slotProps.data[column.field], column.numberFormat) }}</span>
          <span v-else>{{ slotProps.data[column.field] }}</span>
        </template>
        <template #loading>
          <span class="loading-text" />
        </template>
      </mui-table-column>
    </template>
  </mui-table>
</template>

<script>
import MuiTableColumn from 'primevue/column/';
import MuiTable from './table/MuiTable';

export default {
  name: 'MuiGrid',
  components: {
    'mui-table': MuiTable,
    'mui-table-column': MuiTableColumn,
  },
  props: {
    columns: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },
    scrollable: { type: Boolean, default: false },
    scrollHeight: { type: String, default: 'auto' },
    frozenWidth: { type: String, default: '' },
    expandableRowGroups: { type: Boolean, default: false },
    rowGroupMode: { type: String, default: '' },
    groupRowsBy: { type: [Array, String], default: null },
    sortMode: { type: String, default: '' },
    sortField: { type: String, default: '' },
    sortOrder: { type: Number, default: 1 },
  },

  data() {
    return {
      tempDataList: [],
      dataList: [],
      lazyTotalRecords: 0,
      expandedRowGroups: null,
      groupSum: null,
      isSortCall: false,
    };
  },

  computed: {
    compFrozenWidth() {
      const frozenColumns = this.$props.columns.filter(col => col.frozenYn === 'Y' || col.groupedYn === 'Y');
      const groupedColumns = this.$props.columns.filter(col => col.groupedYn === 'Y');
      return `${frozenColumns.length * 150 + groupedColumns.length * 25}px`;
    },
    compGroupRowsBy() {
      const groupedColumns = this.$props.columns.filter(col => col.groupedYn === 'Y').map(col => col.field);
      if (groupedColumns.length === 0) {
        return null;
      }
      return groupedColumns;
    },
    gridColumns() {
      const columns = this.$props.columns.map(col => {
        const column = {};
        Object.keys(col).forEach(key => {
          column[key] = col[key];
        });
        column.headerStyle = {
          width: col.groupedYn === 'Y' ? '175px' : '150px',
          height: '45px',
          'text-align': 'center',
          'white-space': 'nowrap',
          overflow: 'hidden',
          'text-overflow': 'ellipsis',
        };
        column.bodyStyle = {
          width: col.groupedYn === 'Y' ? '175px' : '150px',
          'text-align': col.isMeasure ? 'right' : 'left',
          'white-space': 'nowrap',
          overflow: 'hidden',
          'text-overflow': 'ellipsis',
        };
        return column;
      });
      return columns;
    },
  },

  watch: {
    data(newValue) {
      if (newValue.length > 0) {
        this.initData(newValue);
      } else {
        this.dataList = newValue;
      }

      if (this.isSortCall) {
        this.isSortCall = false;
      } else {
        this.$refs.MuiTable.d_sortField = '';
        this.$refs.MuiTable.d_sortOrder = 1;
      }
    },
  },

  created() {
    this.gridRef = `gridRef${_.uniqueId()}`;
  },

  mounted() {
    this.initData(this.data);
  },

  methods: {
    initData(data) {
      const dataList = [];
      const allValue = '(All)';
      const dimensionColumns = this.columns.filter(column => !column.isMeasure);
      if (this.compGroupRowsBy == null) {
        if (dimensionColumns.length > 0) {
          const lastDimensionField = dimensionColumns[dimensionColumns.length - 1].field;
          data.forEach(item => {
            if (item[lastDimensionField] !== allValue) {
              dataList.push(item);
            }
          });
          this.tempDataList = dataList.slice();
          this.lazyTotalRecords = dataList.length;
          this.dataList = dataList.slice(0, 40);
        }
      } else {
        const lastDimensionField = dimensionColumns[dimensionColumns.length - 1].field;
        this.groupSum = {};
        data.forEach(item => {
          if (item[lastDimensionField] === allValue) {
            const isGroupSum = dimensionColumns.every(column => {
              if (this.compGroupRowsBy.indexOf(column.field) > -1) {
                return true;
              }
              return item[column.field] === allValue;
            });

            if (isGroupSum) {
              const groupField = this.compGroupRowsBy[0];
              const groupFieldValue = item[groupField];
              this.groupSum[groupFieldValue] = item;
            }
          } else {
            dataList.push(item);
          }
        });
        this.tempDataList = dataList.slice();
        this.dataList = dataList.slice(0, dataList.length);
      }
    },
    onVirtualScroll(event) {
      this.dataList = this.tempDataList.slice(event.first, event.first + event.rows);
    },
    onSort(event) {
      this.$emit('sort', event);
      this.isSortCall = true;
    },
    getNumberFormat(number, numberFormat) {
      let value = number;
      if (numberFormat === 'PCNT') {
        value = parseFloat(value).toFixed(2);
        return `${utils.numberFormat(value)}%`;
      }
      return utils.numberFormat(value);
    },
  },
};
</script>
